import type { SupabaseClient } from '@supabase/supabase-js'
import {
  cancelOperationResultSchema,
  cancelRoutePassResultSchema,
  prepareRoutePassesResultSchema,
  routePassesOverviewSchema,
  closeDiagnosticSchema,
  closeWithExceptionsResultSchema,
  missionHeaderResultSchema,
  operationDetailSchema,
  operationJournalSchema,
  operationResidencesSchema,
  operationsListV2Schema,
  operationCommandResultSchema,
  operationsCenterChangesSchema,
  operationsCenterSnapshotSchema,
  operationsListSchema,
  operationStatsSchema,
  operationTaskCommandResultSchema,
  operationTaskDetailSchema,
  operationTasksSchema,
  operationTracksSchema,
  publishOfferResultSchema,
} from '@groupe-reca/contracts'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { OperationsCenterRepository } from '../../application/OperationsCenterRepository'
import { OperationsCenterError, operationsErrorCode } from '../../domain/operationsCenter'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

/** Filtres facultatifs : clés absentes plutôt que vides (le serveur refuse les clés inconnues, pas les absentes). */
function withoutEmpty(command: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(command).filter(([, value]) => value !== undefined && value !== ''),
  )
}

export function createSupabaseOperationsCenterRepository(
  client: AppSupabaseClient,
): OperationsCenterRepository {
  // Fonctions des migrations 20261101010000 à 20261101040000, absentes des types générés : réponses validées par
  // les contrats 0.5.0.
  const rpcClient = client as unknown as SupabaseClient

  async function call(name: string, command: Record<string, unknown>): Promise<unknown> {
    const response = (await rpcClient.rpc(name, { p_command: command })) as unknown as RpcResponse
    if (response.error) throw new OperationsCenterError(operationsErrorCode(response.error.message))
    return response.data
  }

  return {
    async snapshot(operationId) {
      return operationsCenterSnapshotSchema.parse(
        await call('operations_center_snapshot_v1', operationId ? { operationId } : {}),
      )
    },
    async changes(since, operationId) {
      return operationsCenterChangesSchema.parse(
        await call('operations_center_changes_v1', operationId ? { since, operationId } : { since }),
      )
    },
    async tracks(operationId, missionIds) {
      return operationTracksSchema.parse(
        await call('operation_tracks_v1', missionIds ? { operationId, missionIds } : { operationId }),
      )
    },
    async stats(operationId) {
      return operationStatsSchema.parse(await call('operation_stats_v1', { operationId }))
    },
    async taskDetail(taskId) {
      return operationTaskDetailSchema.parse(await call('operation_task_detail_v1', { taskId }))
    },
    async tasks(operationId, status, search) {
      return operationTasksSchema.parse(
        await call('operation_tasks_v1', search ? { operationId, status, search } : { operationId, status }),
      )
    },
    async operations(page) {
      return operationsListSchema.parse(await call('operations_list_v1', { page }))
    },
    async closeOperation(command) {
      return operationCommandResultSchema.parse(await call('close_operation_v1', { ...command }))
    },
    async reopenOperation(command) {
      return operationCommandResultSchema.parse(await call('reopen_operation_v1', { ...command }))
    },
    async cancelMission(command) {
      missionHeaderResultSchema.parse(await call('cancel_mission_v1', { ...command }))
    },
    async retryTask(command) {
      return operationTaskCommandResultSchema.parse(await call('retry_operation_task_v1', { ...command }))
    },
    async resolveTask(command) {
      return operationTaskCommandResultSchema.parse(
        await call('resolve_operation_task_without_service_v1', { ...command }),
      )
    },
    async resetTask(command) {
      return operationTaskCommandResultSchema.parse(
        await call('reset_operation_task_todo_v1', { ...command }),
      )
    },
    async reassignTask(command) {
      return operationTaskCommandResultSchema.parse(await call('reassign_operation_task_v1', { ...command }))
    },
    async publishOffer(command) {
      return publishOfferResultSchema.parse(await call('publish_reinforcement_offer_v1', { ...command }))
    },
    async revokeOffer(command) {
      await call('revoke_reinforcement_offer_v1', { ...command })
    },
    async acknowledgeHelp(command) {
      await call('acknowledge_help_request_v1', { ...command })
    },
    async resolveHelp(command) {
      await call('resolve_help_request_v1', { ...command })
    },
    async acknowledgeWatch(incidentKey) {
      await call('acknowledge_watch_item_v1', { incidentKey })
    },
    async resolveConflict(command) {
      await call('resolve_operation_conflict_v1', { ...command })
    },
    async operationsModule(query) {
      return operationsListV2Schema.parse(await call('operations_list_v2', withoutEmpty({ ...query })))
    },
    async operationDetail(operationId) {
      return operationDetailSchema.parse(await call('operation_detail_v1', { operationId }))
    },
    async closeDiagnostic(operationId) {
      return closeDiagnosticSchema.parse(await call('operation_close_diagnostic_v1', { operationId }))
    },
    async closeWithExceptions(command) {
      return closeWithExceptionsResultSchema.parse(
        await call('close_operation_with_exceptions_v1', { ...command }),
      )
    },
    async cancelOperation(command) {
      return cancelOperationResultSchema.parse(await call('cancel_operation_v1', { ...command }))
    },
    async residences(query) {
      return operationResidencesSchema.parse(
        await call('operation_residences_v1', withoutEmpty({ ...query })),
      )
    },
    async routePasses(operationId) {
      return routePassesOverviewSchema.parse(await call('operation_route_passes_v1', { operationId }))
    },
    async preparePasses(command) {
      return prepareRoutePassesResultSchema.parse(await call('prepare_route_passes_v1', { ...command }))
    },
    async cancelPass(command) {
      cancelRoutePassResultSchema.parse(await call('cancel_route_pass_v1', { ...command }))
    },
    async journal(operationId, beforeId) {
      return operationJournalSchema.parse(
        await call('operation_journal_v1', beforeId ? { operationId, beforeId } : { operationId }),
      )
    },
  }
}
